/*
 * WHAT MAY BE ATTACHED.
 *
 * These rules MIRROR the worker's (`convfiles.ts`: `inlineSafe`, `FILE_MAX_BYTES`). They are a
 * courtesy, not a control — the server refuses on its own and is the only thing standing between a
 * hostile client and the bucket. Checking here exists so a person learns their 40 MB video is not
 * going to work while they are still looking at the file picker, rather than after an upload and a
 * 413.
 *
 * That the two lists have to agree is a real cost, and the honest reason to pay it is that the
 * alternative — a picker that accepts anything and a send that fails — is worse. When the server's
 * list changes, this one changes with it; the test in this package fails if the caps drift.
 *
 * SVG IS DELIBERATELY EXCLUDED. It is an image by extension and a script host in practice, and the
 * worker refuses to inline it for exactly that reason. Offering it here would mean accepting a file
 * the server will then decline to display.
 */
/** One file blob, matching the worker's FILE_MAX_BYTES. */
export const FILE_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Media types the AI plane accepts and can render.
 *
 * `image/*` minus SVG, plus the document types the worker treats as inline-safe. Deliberately
 * short: every entry is something the model can actually read, and offering a type it cannot is a
 * promise the conversation then breaks.
 */
export const ACCEPTED_MEDIA = [
    'image/png',
    'image/jpeg',
    'image/gif',
    'image/webp',
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/json',
];
/** The `accept` attribute for a file input — the same rule expressed the way the OS picker wants
 *  it, so the dialog greys out what would be refused instead of letting it be chosen first. */
export const ACCEPT_ATTR = ACCEPTED_MEDIA.join(',');
/** Is this something we accept? Compares the bare type, since browsers append parameters. */
export function mediaAccepted(mediaType) {
    const m = mediaType.toLowerCase().split(';')[0].trim();
    return ACCEPTED_MEDIA.includes(m);
}
/**
 * Screen a picked batch.
 *
 * Returns BOTH halves rather than silently dropping the bad ones: a file that vanishes without
 * explanation reads as a broken picker, and the user is left to guess whether it was the type, the
 * size, or the app. The caller says which it was.
 */
export function screenFiles(files) {
    const accepted = [];
    const rejected = [];
    for (const file of files) {
        if (!mediaAccepted(file.type))
            rejected.push({ name: file.name, reason: 'type' });
        else if (file.size > FILE_MAX_BYTES)
            rejected.push({ name: file.name, reason: 'size' });
        else
            accepted.push({ file, id: `${file.name}:${file.size}:${file.lastModified}` });
    }
    return { accepted, rejected };
}
/**
 * Add a picked batch to what is already staged — the ONE rule both composers use.
 *
 * De-duplicated by identity (picking the same file twice is a slip, and two identical chips are a
 * puzzle rather than a feature), and the rejections come back as one sentence the caller shows,
 * so the person learns it was the type or the size rather than watching a file vanish. `t` is
 * passed in because this module is pure and the sentence is not.
 */
export function stagePicked(current, picked, t) {
    const { accepted, rejected } = screenFiles(picked);
    const have = new Set(current.map((f) => f.id));
    return {
        files: [...current, ...accepted.filter((f) => !have.has(f.id))],
        rejected: rejected.map((r) => t(r.reason === 'type' ? 'ai.rejectType' : 'ai.rejectSize', { name: r.name })).join(' '),
    };
}
/** Human size for a chip — "812 KB", "2.4 MB". */
export function fmtSize(bytes) {
    if (bytes < 1024)
        return `${bytes} B`;
    if (bytes < 1024 * 1024)
        return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
//# sourceMappingURL=attachments.js.map